import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { applyConnectLink, type ConnectFragment, type ShowDock } from '../../net/connect-link';
import { useEditedSceneId, useRuntime, useUi, useUiContext } from '../context';
import { isTypingTarget } from '../hotkeys/chords';
import { HotkeyManager } from '../hotkeys/HotkeyManager';
import { DockLayout } from './DockLayout';
import { portalOrigin } from '../embed/embedBridge';
import { createEmbedBridge, postAdOfferDecision } from '../embed/embedBridge';
import { createMonetizationSync } from '../embed/monetization';
import { nativeToolsStore } from '../../nativeTools/toolsPresence';
import { startNativeToolsPresence } from '../../nativeTools/browserConnection';
import { adOfferStore, formatMoney } from '../panels/ad-offers/model';
import { applyDocumentTheme, lightSchemeQuery } from '../theme/theme';
import { selectTheme, type UiStore } from '../uiStore';
import { DialogHost, MenuHost } from './Hosts';
import { MenuBar } from './MenuBar';
import { safely } from '../safe';
import { StatusBar } from './StatusBar';
import { Toasts } from './Toasts';
import { prompterFrameHeight, prompterFrameLink, prompterFrameNative } from './prompterFrame';
import '../theme/tokens.css';
import '../theme/base.css';
import './shell.css';

function FatalScreen({ message }: { message: string }): JSX.Element {
  return (
    <div className="screen" role="alert">
      <h1>The studio could not start</h1>
      <pre>{message}</pre>
      <p>
        This usually means the browser has no usable GPU backend or blocked access to storage. Check that hardware acceleration is
        enabled, then reload.
      </p>
      <button type="button" className="ui-btn primary" onClick={() => window.location.reload()}>
        Reload
      </button>
    </div>
  );
}

function LoadingScreen(): JSX.Element {
  return (
    <div className="screen" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <span>Starting the studio…</span>
    </div>
  );
}

/** Clears state that belongs to one scene or one collection when it stops applying. */
function SelectionGuard(): null {
  const { ui } = useUiContext();
  const sceneId = useEditedSceneId();
  const collectionId = useRuntime((r) => r.collection.id);
  useEffect(() => {
    ui.getState().select([]);
  }, [ui, sceneId]);
  useEffect(() => {
    ui.getState().setClipboard({ items: null });
  }, [ui, collectionId]);
  return null;
}

/** Keeps `data-theme` on <html> in step with the resolved theme, and follows OS colour-scheme changes. */
function ThemeHost(): null {
  const { ui } = useUiContext();
  const theme = useUi(selectTheme);
  useEffect(() => applyDocumentTheme(theme), [theme]);
  useEffect(() => {
    const query = lightSchemeQuery();
    if (!query) return;
    const onChange = (): void => ui.getState().setSystemLight(query.matches);
    onChange();
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, [ui]);
  return null;
}

/** Brings up what `omp:show-dock` / `#dock=` asks for; the Collaboration card is scrolled to once the panel has rendered. */
function showDock(ui: UiStore, dock: ShowDock): void {
  if (dock === 'tools') {
    ui.getState().openDialog({ type: 'streaming-tools' });
    return;
  }
  ui.getState().dock?.show('stream-manager');
  if (dock === 'collab') {
    setTimeout(() => {
      const card = document.getElementById('sm-collab');
      card?.scrollIntoView?.({ block: 'center' });
      card?.focus?.({ preventScroll: true });
    }, 50);
  }
}

/**
 * Inside the portal's creator dashboard: reports status to the portal and takes its theme. It also shows the
 * creator's ad offers the portal sends, and hands the creator's answers back for the portal to make.
 */
function ToolsPresenceHost(): null {
  useEffect(() => startNativeToolsPresence(), []);
  return null;
}

function EmbedHost(): null {
  const { studio, ui } = useUiContext();
  const embedded = useUi((s) => s.embedded);
  useEffect(() => {
    if (!embedded || window.parent === window) return;
    const parent = window.parent;
    const monetization = createMonetizationSync(studio);
    const offers = adOfferStore.getState();
    // Native = ad-offer presence OR the portal prompter reporting link 'native'; both reset when the bridge stops.
    const gate = { presence: false, link: false };
    const reportNative = (): void => studio.reportNativeOperational(gate.presence || gate.link);
    offers.setDecider((offerId, decision) => void postAdOfferDecision(parent, studio, offerId, decision));
    const stop = createEmbedBridge({
      studio, parent, self: window, onTheme: (theme) => ui.getState().setParentTheme(theme),
      onShowDock: (dock) => showDock(ui, dock),
      onAdOffer: (message) => {
        // While omp-tools runs it shows the offer natively: kept, but no toast and no dock.
        if (!adOfferStore.getState().receive(message, Date.now()) || adOfferStore.getState().native) return;
        const { brand, net_cents, currency } = message.offer;
        studio.notify('info', `Oferta de anuncio de ${brand.name}: ganás ${formatMoney(net_cents, currency)}. Respondé antes de que venza.`);
        // No dock pop-up: the floating card is the portal's, at the top centre over Studio (the dock used to open
        // by itself in the bottom dock row, so the offer showed up at the bottom).
      },
      onAdOfferResolved: (resolved) => adOfferStore.getState().resolve(resolved, Date.now()),
      getToolsPresence: () => nativeToolsStore.getState().connected,
      onAdOfferPresence: (native) => { adOfferStore.getState().setNative(native); gate.presence = native; reportNative(); },
      onPrompterNative: (native) => { gate.link = native; reportNative(); },
      onPrompterLink: (link) => studio.reportPrompterLink(link),
      onMonetization: (enabled) => { void monetization.setEnabled(enabled); },
    });
    return () => {
      stop();
      studio.reportNativeOperational(false);
      monetization.dispose();
      adOfferStore.getState().setDecider(null);
    };
  }, [embedded, studio, ui]);
  return null;
}

/**
 * Private text remains in the portal's authenticated iframe, isolated from Studio scripts. Without an offer the
 * frame is a 1 px, click-through, transparent speck (it must stay in the viewport: the portal proves presentability
 * from inside it); with one it floats at the top centre of the stage (toasts sit above, z 3000), sized by the card's
 * reported height. The connection is never drawn here: it is the OMP Advertising source's status dot.
 * Only standalone: embedded in the portal, the portal's own dashboard shows the card (and the Ad Offers dock).
 */
function PrivatePrompterHost(): JSX.Element | null {
  const {studio}=useUiContext(); const embedded=useUi(s=>s.embedded);
  const frame=useRef<HTMLIFrameElement>(null); const [open,setOpen]=useState(false); const [height,setHeight]=useState<number|null>(null);
  const [origin,setOrigin]=useState<string|null>(()=>portalOrigin(studio));
  useEffect(()=>studio.project.subscribe(()=>setOrigin(portalOrigin(studio))),[studio]);
  useEffect(()=>{
    const receive=(e:MessageEvent)=>{
      if(e.source!==frame.current?.contentWindow||e.origin!==origin||e.data?.type!=='omp:prompter-state'||typeof e.data.open!=='boolean')return;
      setOpen(e.data.open);setHeight(prompterFrameHeight(e.data.height));
      studio.reportPrompterLink(prompterFrameLink(e.data.link)??'none');
      studio.reportNativeOperational(prompterFrameNative(e.data.link));
    };
    window.addEventListener('message',receive);return()=>window.removeEventListener('message',receive);
  },[origin,studio]);
  const hosting=!embedded&&!!origin;
  // Standalone only: embedded, the portal's own prompter reports the link through the embed bridge.
  useEffect(()=>{if(!hosting)return; studio.reportPrompterLink('connecting'); return()=>{studio.reportPrompterLink('none'); studio.reportNativeOperational(false);};},[hosting,studio]);
  // The frame draws its cards in the theme opposite to Studio's: it is told Studio's theme (portal origin only).
  const theme=useUi(selectTheme);
  const postTheme=useCallback(()=>{if(origin)frame.current?.contentWindow?.postMessage({type:'omp:theme',theme},origin);},[origin,theme]);
  useEffect(()=>postTheme(),[postTheme]);
  if(!hosting)return null;
  return <iframe ref={frame} title="Private advertising prompter" src={`${origin}/popout/studio-prompter`} onLoad={postTheme} style={{position:'fixed',top:40,left:'50%',transform:'translateX(-50%)',width:open?456:1,maxWidth:'calc(100vw - 28px)',height:open?(height??360):1,maxHeight:'calc(100vh - 56px)',border:0,zIndex:1000,background:'transparent',colorScheme:'normal',pointerEvents:open?'auto':'none'}} />;
}

/** Applies a `#connect=` link once the dock exists: stores the key, names the channel, opens the Stream Manager. */
function ConnectLinkHost({ fragment }: { fragment?: ConnectFragment }): null {
  const { studio } = useUiContext();
  const dock = useUi((s) => s.dock);
  const done = useRef(false);
  useEffect(() => {
    if (!fragment?.present || !dock || done.current) return;
    done.current = true;
    if (!fragment.link) {
      studio.notify('error', 'The connect link is not valid. Open it again from Settings → Stream on the portal.');
      return;
    }
    const link = fragment.link;
    void safely(studio, () => applyConnectLink(studio, link), 'Could not connect to the channel');
    dock.show('stream-manager');
  }, [fragment, dock, studio]);
  return null;
}

/** Applies a `#dock=` request once the dock exists. */
function DockRequestHost({ dock: requested }: { dock?: ShowDock | null }): null {
  const { ui } = useUiContext();
  const dock = useUi((s) => s.dock);
  const done = useRef(false);
  useEffect(() => {
    if (!requested || !dock || done.current) return;
    done.current = true;
    showDock(ui, requested);
  }, [requested, dock, ui]);
  return null;
}

function Workspace({ connect, dock }: { connect?: ConnectFragment; dock?: ShowDock | null }): JSX.Element {
  return (
    <HotkeyManager>
      <SelectionGuard />
      <PrivatePrompterHost />
      <ConnectLinkHost fragment={connect} />
      <DockRequestHost dock={dock} />
      <ErrorBoundary name="The menu bar">
        <MenuBar connect={connect} />
      </ErrorBoundary>
      <DockLayout />
      <ErrorBoundary name="The status bar">
        <StatusBar />
      </ErrorBoundary>
      <DialogHost />
      <MenuHost />
    </HotkeyManager>
  );
}

export function App({ connect, dock }: { connect?: ConnectFragment; dock?: ShowDock | null }): JSX.Element {
  const ready = useRuntime((r) => r.ready);
  const fatalError = useRuntime((r) => r.fatalError);
  return (
    <div
      className="studio-root"
      onContextMenu={(e) => {
        // Keep the browser menu where it is useful: text fields and selectable text.
        const target = e.target instanceof Element ? e.target : null;
        if (!isTypingTarget(target) && !target?.closest('.ui-selectable')) e.preventDefault();
      }}
    >
      <ThemeHost />
      <ToolsPresenceHost />
      <EmbedHost />
      {fatalError ? <FatalScreen message={fatalError} /> : ready ? <Workspace connect={connect} dock={dock} /> : <LoadingScreen />}
      <Toasts />
    </div>
  );
}
